import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

// === TYPES ===

export interface MessageSender {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
}

export interface MessageAttachment {
  id: string;
  filename: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
}

export interface MessageMention {
  id: string;
  userId: string;
  user: { id: string; firstName: string; lastName: string };
  readAt?: string;
}

export interface Message {
  id: string;
  dossierId: string;
  milestoneId?: string;
  senderType: 'customer' | 'agent' | 'system' | 'field_worker';
  senderId?: string;
  sender?: MessageSender;
  channel: 'app' | 'whatsapp' | 'email' | 'sms' | 'system';
  messageType: 'message' | 'internal' | 'system' | 'milestone_change';
  content: string;
  metadata?: string;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
  whatsappStatus?: string | null;
  emailMessageId?: string | null;
  threadId?: string | null;
  inReplyTo?: string | null;
  conversationId?: string | null;
  archived?: boolean;
  assignedToId?: string | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  attachments: MessageAttachment[];
  mentions: MessageMention[];
  milestone?: {
    id: string;
    template?: { naam: string; icon: string };
  };
}

export interface InboxItem {
  id: string;
  dossierNummer: string;
  klantNaam: string;
  type: string;
  status: string;
  verantwoordelijkeId: string | null;
  commLabel: string | null;
  activeMilestone: {
    id: string;
    naam: string;
    icon: string;
  } | null;
  lastMessage: {
    id: string;
    content: string;
    channel: string;
    senderName: string;
    createdAt: string;
    messageType: string;
  } | null;
  unreadCount: number;
  updatedAt: string;
}

export interface ChannelCounts {
  total: number;
  app?: number;
  whatsapp?: number;
  email?: number;
  sms?: number;
  system?: number;
}

export interface DossierContact {
  id: string;
  dossierId: string;
  name: string;
  role?: string;
  phone?: string;
  email?: string;
  whatsappAvailable: boolean;
  preferredChannel: string;
}

export interface MilestoneDateItem {
  milestoneId: string;
  milestoneName: string;
  milestoneIcon: string;
  milestoneStatus: string;
  volgorde: number;
  geplandeDatum?: string;
  milestoneDate: {
    id: string;
    scheduledDate?: string;
    confirmed: boolean;
    confirmedAt?: string;
    confirmedByUser?: { id: string; firstName: string; lastName: string };
  } | null;
}

export interface DocumentGroup {
  milestoneId: string | null;
  milestoneName: string | null;
  documents: DossierDoc[];
}

export interface DossierDoc {
  id: string;
  filename: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  signed: boolean;
  signedAt?: string;
  milestoneId?: string;
  createdAt: string;
}

export interface OutlookStatus {
  connected: boolean;
  emailAddress: string | null;
  syncEnabled: boolean;
  lastSyncAt: string | null;
}

export interface TemplateBlock {
  id: string;
  type: 'header' | 'text' | 'image' | 'button' | 'footer' | 'divider' | 'spacer';
  content: string;
  settings: Record<string, any>;
}

export interface MessageTemplate {
  id: string;
  name: string;
  channel: string;
  subject?: string;
  content: string;
  blocks?: TemplateBlock[] | null;
  categorie?: string | null;
  variabelen?: Array<{ key: string; label: string }> | null;
  triggerEvent?: string;
  triggerMilestoneKey?: string;
  createdById?: string | null;
  active: boolean;
  updatedAt: string;
  createdAt: string;
}

export interface DossierPreview {
  id: string;
  dossierNummer: string;
  klantNaam: string;
  stad: string;
}

// === HOOKS ===

export function useInbox(filter?: string, search?: string, channel?: string, label?: string, mijn?: boolean) {
  const params = new URLSearchParams();
  if (filter) params.set('filter', filter);
  if (search) params.set('search', search);
  if (channel && channel !== 'all') params.set('channel', channel);
  if (label) params.set('label', label);
  if (mijn) params.set('mijn', 'true');
  const qs = params.toString();

  return useQuery<InboxItem[]>({
    queryKey: ['communicatie', 'inbox', filter, search, channel, label, mijn],
    queryFn: () => apiFetch(`/api/communicatie/inbox${qs ? `?${qs}` : ''}`),
    refetchInterval: 15000,
  });
}

export function useChannelCounts() {
  return useQuery<ChannelCounts>({
    queryKey: ['communicatie', 'inbox', 'counts'],
    queryFn: () => apiFetch('/api/communicatie/inbox/counts'),
    refetchInterval: 30000,
  });
}

export function useUpdateCommLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; label: string | null }) =>
      apiFetch(`/api/communicatie/inbox/dossier/${data.dossierId}/label`, {
        method: 'PATCH',
        body: JSON.stringify({ label: data.label }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'inbox'] });
    },
  });
}

export function useBulkMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dossierIds: string[]) =>
      apiFetch('/api/communicatie/inbox/bulk-read', {
        method: 'POST',
        body: JSON.stringify({ dossierIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie'] });
    },
  });
}

export function useBulkReadMessages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageIds: string[]) =>
      apiFetch('/api/communicatie/dossiers/bulk-read', {
        method: 'POST',
        body: JSON.stringify({ messageIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'messages'] });
      qc.invalidateQueries({ queryKey: ['communicatie', 'inbox'] });
    },
  });
}

export function useBulkAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierIds: string[]; userId: string }) =>
      apiFetch('/api/communicatie/inbox/bulk-assign', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'inbox'] });
    },
  });
}

export function useMessages(dossierId: string | null, channel?: string) {
  const params = new URLSearchParams();
  if (channel) params.set('channel', channel);
  const qs = params.toString();

  return useQuery<{ messages: Message[]; dossierId: string }>({
    queryKey: ['communicatie', 'messages', dossierId, channel],
    queryFn: () => apiFetch(`/api/communicatie/dossiers/${dossierId}/messages${qs ? `?${qs}` : ''}`),
    enabled: !!dossierId,
    refetchInterval: 10000,
  });
}

interface MessagesPage {
  messages: Message[];
  dossierId: string;
  nextCursor: string | null;
  hasMore: boolean;
}

export function useInfiniteMessages(dossierId: string | null, channel?: string) {
  const params = new URLSearchParams();
  if (channel) params.set('channel', channel);
  params.set('limit', '50');

  return useInfiniteQuery<MessagesPage>({
    queryKey: ['communicatie', 'messages', 'infinite', dossierId, channel],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(params);
      if (pageParam) p.set('cursor', pageParam as string);
      return apiFetch(`/api/communicatie/dossiers/${dossierId}/messages?${p.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!dossierId,
    refetchInterval: 10000,
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; content: string; channel?: string; messageType?: string; milestoneId?: string; metadata?: any }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'messages', vars.dossierId] });
      qc.invalidateQueries({ queryKey: ['communicatie', 'inbox'] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; messageId: string }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/messages/${data.messageId}/read`, { method: 'PATCH' }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'messages', vars.dossierId] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dossierId: string) =>
      apiFetch(`/api/communicatie/dossiers/${dossierId}/messages/read-all`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie'] });
    },
  });
}

/** dataTransfer-type bij het slepen van een dossierdocument naar het gesprek. */
export const DOCUMENT_SLEEP_TYPE = 'application/x-kordaro-document';

export interface DossierDocument {
  id: string;
  naam: string;
  mimeType: string;
  bestandsgrootte: number;
  documentType: string | null;
  bron: string;
  createdAt: string;
  klasse: 'deelbaar' | 'intern' | 'beheer';
  deelbaar: boolean;
}

/** Een document dat klaarstaat om mee te sturen met het volgende bericht aan de klant. */
export interface KlaarDocument {
  id: string;
  naam: string;
  mimeType: string;
}

/** De echte documenten van het dossier (`Document`), al gefilterd op wat deze gebruiker mag zien. */
export function useDossierDocumenten(dossierId: string | null) {
  return useQuery<DossierDocument[]>({
    queryKey: ['communicatie', 'dossierdocumenten', dossierId],
    queryFn: () => apiFetch(`/api/communicatie/dossiers/${dossierId}/dossierdocumenten`),
    enabled: !!dossierId,
  });
}

export function useDeelDocumenten() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; documentIds: string[]; channel: string; tekst?: string }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/documenten-delen`, {
        method: 'POST',
        body: JSON.stringify({ documentIds: data.documentIds, channel: data.channel, tekst: data.tekst }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'messages', vars.dossierId] });
      qc.invalidateQueries({ queryKey: ['communicatie', 'inbox'] });
    },
  });
}

export function useDossierDocuments(dossierId: string | null) {
  return useQuery<DocumentGroup[]>({
    queryKey: ['communicatie', 'documents', dossierId],
    queryFn: () => apiFetch(`/api/communicatie/dossiers/${dossierId}/documents`),
    enabled: !!dossierId,
  });
}

export function useMilestoneDates(dossierId: string | null) {
  return useQuery<MilestoneDateItem[]>({
    queryKey: ['communicatie', 'dates', dossierId],
    queryFn: () => apiFetch(`/api/communicatie/dossiers/${dossierId}/dates`),
    enabled: !!dossierId,
  });
}

export function useUpdateMilestoneDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; milestoneId: string; scheduledDate?: string; confirmed?: boolean }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/dates/${data.milestoneId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'dates', vars.dossierId] });
    },
  });
}

export function useDossierContacts(dossierId: string | null) {
  return useQuery<DossierContact[]>({
    queryKey: ['communicatie', 'contacts', dossierId],
    queryFn: () => apiFetch(`/api/communicatie/dossiers/${dossierId}/contacts`),
    enabled: !!dossierId,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; name: string; role?: string; phone?: string; email?: string; whatsappAvailable?: boolean; preferredChannel?: string }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'contacts', vars.dossierId] });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { dossierId: string; contactId: string }) =>
      apiFetch(`/api/communicatie/dossiers/${data.dossierId}/contacts/${data.contactId}`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['communicatie', 'contacts', vars.dossierId] });
    },
  });
}

export function useOutlookStatus() {
  return useQuery<OutlookStatus>({
    queryKey: ['communicatie', 'outlook'],
    queryFn: () => apiFetch('/api/communicatie/outlook/status'),
  });
}

export function useTemplates() {
  return useQuery<MessageTemplate[]>({
    queryKey: ['communicatie', 'templates'],
    queryFn: () => apiFetch('/api/communicatie/templates'),
  });
}

export function useRenderTemplate() {
  return useMutation({
    mutationFn: (data: { templateId: string; dossierId: string }) =>
      apiFetch<{ html: string; text: string; content: string; subject: string; channel: string }>(`/api/communicatie/templates/${data.templateId}/render`, {
        method: 'POST',
        body: JSON.stringify({ dossierId: data.dossierId }),
      }),
  });
}

export function useTemplate(id: string | null) {
  return useQuery<MessageTemplate>({
    queryKey: ['communicatie', 'templates', id],
    queryFn: () => apiFetch(`/api/communicatie/templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<MessageTemplate>) =>
      apiFetch<MessageTemplate>('/api/communicatie/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'templates'] }); },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string } & Partial<MessageTemplate>) =>
      apiFetch<MessageTemplate>(`/api/communicatie/templates/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'templates'] }); },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/communicatie/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'templates'] }); },
  });
}

export function useRenderBlocks() {
  return useMutation({
    mutationFn: (data: { blocks: TemplateBlock[]; subject?: string; dossierId?: string }) =>
      apiFetch<{ html: string; text: string; subject: string }>('/api/communicatie/templates/render-blocks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function useTemplateDossiers(query: string) {
  return useQuery<DossierPreview[]>({
    queryKey: ['communicatie', 'templates', 'dossiers', query],
    queryFn: () => apiFetch(`/api/communicatie/templates/dossiers/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
  });
}

// === WORKFLOW / AUTOMATION HOOKS ===

export interface AutomationRule {
  id: string;
  entityId: string;
  naam: string;
  beschrijving: string | null;
  trigger: string;
  action: string;
  active: boolean;
  triggerConfig: Record<string, any>;
  actionConfig: Record<string, any>;
  volgorde: number;
  createdAt: string;
  updatedAt: string;
  _count?: { executions: number };
}

export interface WorkflowExecution {
  id: string;
  ruleId: string;
  dossierId: string | null;
  trajectstapId: string | null;
  status: string;
  resultaat: string | null;
  foutmelding: string | null;
  createdAt: string;
}

export function useWorkflowRules() {
  return useQuery<AutomationRule[]>({
    queryKey: ['automation', 'rules'],
    queryFn: () => apiFetch('/api/automation/rules'),
  });
}

export function useCreateWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AutomationRule>) =>
      apiFetch<AutomationRule>('/api/automation/rules', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automation', 'rules'] }); },
  });
}

export function useUpdateWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string } & Partial<AutomationRule>) =>
      apiFetch<AutomationRule>(`/api/automation/rules/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automation', 'rules'] }); },
  });
}

export function useDeleteWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/automation/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automation', 'rules'] }); },
  });
}

export function useWorkflowLog(ruleId: string | null) {
  return useQuery<WorkflowExecution[]>({
    queryKey: ['automation', 'rules', ruleId, 'log'],
    queryFn: () => apiFetch(`/api/automation/rules/${ruleId}/log`),
    enabled: !!ruleId,
  });
}

export function useTestWorkflow() {
  return useMutation({
    mutationFn: (data: { ruleId: string; dossierId?: string }) =>
      apiFetch<{ success: boolean; uitgevoerd: number; execution: WorkflowExecution | null }>('/api/communicatie/workflows/test', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function useAutomationTemplates() {
  return useQuery<Array<{ id: string; name: string; subject: string | null; channel: string }>>({
    queryKey: ['automation', 'templates'],
    queryFn: () => apiFetch('/api/automation/templates'),
  });
}

// === DOSSIER COMMUNICATIE ===

export interface DossierCommunicatieSummary {
  dossier: { id: string; dossierNummer: string; klantNaam: string; telefoon: string | null; email: string | null };
  channels: Record<string, { count: number; lastMessage: any; unread: number }>;
  totalMessages: number;
  totalUnread: number;
  recentMessages: Array<{
    id: string;
    content: string;
    channel: string;
    senderType: string;
    senderName: string;
    createdAt: string;
    readAt: string | null;
  }>;
}

export function useDossierCommunicatie(dossierId: string | null) {
  return useQuery<DossierCommunicatieSummary>({
    queryKey: ['communicatie', 'dossier', dossierId],
    queryFn: () => apiFetch(`/api/communicatie/inbox/dossier/${dossierId}`),
    enabled: !!dossierId,
    refetchInterval: 15000,
  });
}

export function useKoppelConversatie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { messageId: string; dossierId: string }) =>
      apiFetch(`/api/communicatie/inbox/conversatie/${data.messageId}/koppel`, {
        method: 'POST',
        body: JSON.stringify({ dossierId: data.dossierId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communicatie'] });
    },
  });
}

// === CHATBOT RULES ===

export interface ChatbotRule {
  id: string;
  naam: string;
  triggerType: string;
  triggerWaarde: string;
  antwoord: string;
  kanaal: string;
  prioriteit: number;
  isActief: boolean;
}

export function useChatbotRules() {
  return useQuery<ChatbotRule[]>({
    queryKey: ['communicatie', 'chatbot', 'rules'],
    queryFn: () => apiFetch('/api/communicatie/chatbot/rules'),
  });
}

export function useCreateChatbotRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<ChatbotRule, 'id' | 'isActief'>) =>
      apiFetch('/api/communicatie/chatbot/rules', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'chatbot', 'rules'] }); },
  });
}

export function useUpdateChatbotRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string } & Partial<ChatbotRule>) =>
      apiFetch(`/api/communicatie/chatbot/rules/${data.id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'chatbot', 'rules'] }); },
  });
}

export function useDeleteChatbotRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/communicatie/chatbot/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'chatbot', 'rules'] }); },
  });
}

// === SNELLE ANTWOORDEN ===

export interface SnelAntwoord {
  id: string;
  tekst: string;
  categorie: string | null;
  volgorde: number;
  isActief: boolean;
}

export function useSnelAntwoorden() {
  return useQuery<SnelAntwoord[]>({
    queryKey: ['communicatie', 'chatbot', 'snelantwoorden'],
    queryFn: () => apiFetch('/api/communicatie/chatbot/snelantwoorden'),
  });
}

export function useCreateSnelAntwoord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { tekst: string; categorie?: string; volgorde?: number }) =>
      apiFetch('/api/communicatie/chatbot/snelantwoorden', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'chatbot', 'snelantwoorden'] }); },
  });
}

export function useDeleteSnelAntwoord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/communicatie/chatbot/snelantwoorden/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['communicatie', 'chatbot', 'snelantwoorden'] }); },
  });
}
